import { z } from "zod";

import type { LandingPageId } from "@/cart/landingPackage";
import type { Acquisition } from "@/utils/acquisition";
import type { CohortKey, SweepstakesAnswerKey } from "@/utils/constants";
import type { PopupVariant } from "@/utils/popupOffers";

const ErrorDataSchema = z.object({ msg: z.string() });

const KeepMePostedResponseSchema = z.discriminatedUnion("success", [
  z.object({ success: z.literal(true) }),
  z.object({
    success: z.literal(false),
    error: z.object({
      error_data: z.tuple([ErrorDataSchema], ErrorDataSchema),
    }),
  }),
]);

/** The `/api/keep-me-posted` response body, shared by the route and its callers. */
export type KeepMePostedResponse = z.infer<typeof KeepMePostedResponseSchema>;
export type KeepMePostedErrorResponse = Extract<
  KeepMePostedResponse,
  { success: false }
>;

export type KeepMePostedRequest = {
  email: string;
  acquisition: Acquisition;
  /** Only the cohort picker sends these. */
  selectedOptions?: CohortKey[];
  /** Only the sweepstakes modal sends this. */
  sweepstakesAnswer?: SweepstakesAnswerKey | "none";
  /** The popup test variant the visitor saw; decides draw entry vs gift. */
  popupVariant?: PopupVariant;
  /** Only a landing page's offer field sends this: the offer email to send. */
  landingPage?: LandingPageId;
};

export type KeepMePostedResult =
  { ok: true } | { ok: false; status: number; message: string };

/**
 * Subscribes an email through `/api/keep-me-posted`. Resolves with the route's
 * verdict; rejects when the request never completes or the body is not the
 * route's shape, so callers can tell "the server said no" from "it broke".
 */
export const subscribeEmail = async (
  request: KeepMePostedRequest,
  init: Pick<RequestInit, "keepalive" | "signal"> = {},
): Promise<KeepMePostedResult> => {
  const response = await fetch("/api/keep-me-posted", {
    ...init,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  const body = KeepMePostedResponseSchema.parse(await response.json());
  if (body.success) return { ok: true };
  return {
    ok: false,
    status: response.status,
    message: body.error.error_data[0].msg,
  };
};
